import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2, ChevronDown, Loader2, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { useFormIntegration, type IFormIntegrationConfig } from '~/hooks/useFormIntegration';
import { useSlackChannels } from '~/hooks/useSlackChannels';
import { supabase } from '~/services/supabase';

interface ISlackFormConfigProps {
  formId: string;
  integrationId: string;
}

export const SlackFormConfig = ({ formId, integrationId }: ISlackFormConfigProps) => {
  const { formIntegration, loading, error, save } = useFormIntegration(formId, integrationId);

  const [enabled, setEnabled] = useState(false);
  const [channelId, setChannelId] = useState('');
  const [syncPartialFills, setSyncPartialFills] = useState(true);
  const [partialFillDelay, setPartialFillDelay] = useState(10);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const { channels, loading: channelsLoading, error: channelError } = useSlackChannels(enabled);

  useEffect(() => {
    if (!formIntegration) {
      return;
    }

    setEnabled(formIntegration.enabled);
    const config = formIntegration.config;
    setChannelId(config.channelId ?? '');
    setSyncPartialFills(config.syncPartialFills ?? true);
    setPartialFillDelay(config.partialFillDelayMinutes ?? 10);
  }, [formIntegration]);

  const selectedChannel = useMemo(() => channels.find((channel) => channel.id === channelId), [channels, channelId]);
  const missingRequiredChannel = enabled && syncPartialFills && !channelId;

  const markDirty = (): void => {
    setDirty(true);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const config: IFormIntegrationConfig = {
        channelId: channelId || undefined,
        channelName: selectedChannel?.name,
        syncPartialFills,
        partialFillDelayMinutes: partialFillDelay,
      };

      await save(enabled, config);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async (): Promise<void> => {
    setSendingTest(true);
    try {
      const { error: fnError } = await supabase.functions.invoke('slack-test', {
        body: { formId, channelId },
      });
      if (fnError) throw fnError;
      toast.success('Test message sent! Check the Slack channel.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <CardTitle className="text-base">Slack Notifications</CardTitle>
              {!enabled && (
                <CardDescription>Send partial form completions to a Slack channel after inactivity.</CardDescription>
              )}
            </div>
            {enabled && !expanded && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-green-700 font-medium">Active</span>
                {channelId && <span>&middot; {selectedChannel ? `#${selectedChannel.name}` : 'Channel selected'}</span>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {enabled && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {expanded ? 'Collapse' : 'Edit'}
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => {
                  setEnabled(event.target.checked);
                  if (event.target.checked) {
                    setExpanded(true);
                  }
                  markDirty();
                }}
                className="rounded"
              />
              <span className="text-sm font-medium">Enabled</span>
            </label>
          </div>
        </div>
      </CardHeader>

      {enabled && expanded && (
        <CardContent className="space-y-6">
          {(error || channelError) && <p className="text-sm text-red-600">{error ?? channelError}</p>}

          <div className="space-y-3">
            <h4 className="text-sm font-medium">Slack Channel</h4>
            {channelsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading channels...</span>
              </div>
            ) : channels.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No Slack channels found. Invite the app to a channel, then refresh and try again.
              </p>
            ) : (
              <select
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={channelId}
                onChange={(event) => {
                  setChannelId(event.target.value);
                  markDirty();
                }}
              >
                <option value="">Select a channel...</option>
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    #{channel.name}
                    {channel.isPrivate ? ' (private)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-medium">Partial Fill Settings</h4>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={syncPartialFills}
                onChange={(event) => {
                  setSyncPartialFills(event.target.checked);
                  markDirty();
                }}
                className="rounded"
              />
              <span className="text-sm">Notify Slack for incomplete submissions after inactivity</span>
            </label>
            {syncPartialFills && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Delay:</span>
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={partialFillDelay}
                  onChange={(event) => {
                    setPartialFillDelay(Number(event.target.value));
                    markDirty();
                  }}
                  className="w-24 rounded-md border bg-background px-3 py-1 text-sm"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            )}
          </div>

          {missingRequiredChannel && (
            <p className="text-sm text-amber-700">Select a Slack channel before enabling partial-fill notifications.</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {channelId && !dirty && (
              <Button variant="outline" size="sm" onClick={handleSendTest} disabled={sendingTest}>
                {sendingTest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send Test
              </Button>
            )}
            <Button
              onClick={async () => {
                await handleSave();
                setExpanded(false);
              }}
              disabled={saving || !dirty || missingRequiredChannel}
              size="sm"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Configuration
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
